/**
 * The **Card Binder** gump (trading card game, PRD #493 Slice 3): the player's
 * window onto their whole card collection and deck building, a draggable
 * right-click-to-close DOM gump on the shared {@link createUiWindow} chrome + {@link
 * createTabs} strip (Chassis Slice H, ADR-0271) - the leaderboard-window / Arena-gump
 * family, not a bespoke window. Six tabs:
 *
 *  1. **Collection** - every owned card in a filterable grid, each a rarity-framed
 *     {@link renderCardFace} face carrying its labelled owned count.
 *  2. **Decks** - build and save up to {@link MAX_NAMED_DECKS} named decks of exactly
 *     {@link DECK_SIZE} cards. The editor sits the collection beside the deck with a
 *     mana-curve bar and **cannot build an illegal deck**: it reuses Slice 1's
 *     legality rules ({@link canAddToDeck} / {@link validateDeck} - the single source
 *     of truth) to grey out a card that would exceed 2 copies of a name or 20 cards,
 *     and only owned copies ({@link collectionCount}) can be added.
 *     Each saved legal deck includes a free Practice action against Card Master Carl.
 *  3. **Packs** - the unopened pack pile and authoritative count, with an Open
 *     action when packs are owned and a Buy action that purchases a pack outright
 *     (a `BuyCardPack` intent - no vendor NPC, the server rules price and gold).
 *  4. **Challenges** - three server-authored daily Card Master Carl matchups.
 *  5. **Duel Board** - publish or accept a public card-duel listing.
 *  6. **How to Play** - an always-available learn-to-play guide sourced from the
 *     sim's canonical rules constants and keyword descriptions.
 *
 * It holds **no game logic** (ADR-0268): every rule comes from the pure `sim/` layer,
 * the collection/decks/pack-count are the server's authoritative {@link
 * CardStateMessage} (fed in via {@link CardBinderHandle.setState}), and the gump only
 * renders that state and sends intents through injected callbacks for open, save,
 * pack opening, and pack buying. Reserved-name-only
 * exactly like gold: an anonymous session's state (`reserved: false`) renders the
 * "cards require a reserved name" prompt (PRD story 31) instead of a collection.
 *
 * Pure DOM, `textContent` only (never `innerHTML`); a body-less document degrades to a
 * harmless no-op handle so a bare client never throws.
 */
import {
  cardDef,
  validateDeck,
  canAddToDeck,
  collectionCount,
  deckWithinCollection,
  defaultNamedDeckIndex,
  DECK_SIZE,
  MAX_NAMED_DECKS,
  HERO_START_HP,
  START_MANA,
  MAX_MANA,
  BOARD_SLOTS,
  HAND_LIMIT,
  TURN_TIMER_SECONDS,
  TIMEOUTS_TO_FORFEIT,
  MAX_COPIES_PER_NAME,
  PACK_PRICE_GOLD,
  PACK_ODDS_DISCLOSURE,
  PACK_RARITY_FAIRNESS_DISCLOSURE,
  PACK_DUPLICATE_DISCLOSURE,
  CARD_KEYWORDS,
  cardKeywordText,
  FATIGUE_STEP,
  DEFAULT_OPENING_HAND,
  COIN_CARD_NAME,
  STARTER_RECIPES,
  type CardRarity,
  type StarterRecipeId,
} from './sim/index';
import type {
  WireCardDailyChallenge,
  WireCardEntry,
  WireDeckCard,
  WireNamedDeck,
} from './net/protocol';
import { cardFaceModel, renderCardFace } from './cardFace';
import { createTabs, type TabsHandle } from './ui/tabs';
import { createUiWindow } from './ui/window';
import { createPanelButton, createPromptCard } from './ui/prompt';
import cardPackUrl from '../assets/ui/banksitter-card-pack-normal.png?url';
import deckBritannianUrl from '../assets/ui/banksitter-deck-brit-basics.png?url';
import deckArachnidUrl from '../assets/ui/banksitter-deck-arachnid-swarm.png?url';
import deckUndeadUrl from '../assets/ui/banksitter-deck-undead-requiem.png?url';
import deckDemonUrl from '../assets/ui/banksitter-deck-demo-host.png?url';

/** The pack-wrap art shown on each starter recipe's onboarding choice card. */
const RECIPE_ART_URLS: Record<StarterRecipeId, string> = {
  britannian: deckBritannianUrl,
  arachnid: deckArachnidUrl,
  undead: deckUndeadUrl,
  demon: deckDemonUrl,
};

/** The reserve-a-name nudge shown to an anonymous session (PRD story 31). */
const RESERVE_TEXT = 'Cards require a reserved name — set a passcode on the splash to start collecting.';
/** The empty-collection note (a reserved player who owns nothing - before the starter grant lands). */
const EMPTY_COLLECTION_TEXT = 'No cards yet.';

/** The authoritative Card Binder state the server pushes (the {@link CardStateMessage} data). */
export interface CardBinderState {
  reserved: boolean;
  collection: WireCardEntry[];
  decks: WireNamedDeck[];
  packs: number;
  starterRecipe?: StarterRecipeId | null;
  starterEligible?: boolean;
  dailyChallenges?: WireCardDailyChallenge[];
  dailyChallengeEarnedGold?: number;
  dailyChallengeLimitGold?: number;
  dailyChallengeResetsAt?: number;
}

/** Public, server-authored projection of one player looking for a card duel. */
export interface CardDuelListingView {
  id: string;
  playerName: string;
  wager: number;
  /** Server wall-clock creation time; used only for a friendly waiting-time label. */
  createdAt?: number;
}

/** Authoritative duel-board snapshot, delivered independently of collection state. */
export interface CardDuelListingsState {
  listings: CardDuelListingView[];
  yourListingId?: string;
}

/** Collaborators injected by the installer (kept thin so the gump is unit-testable). */
export interface CardBinderDeps {
  /** The document to wire against; defaults to the global `document` (jsdom-testable). */
  doc?: Document;
  /**
   * The gump opened: request the authoritative state. The installer sends `OpenBinder`,
   * which performs the one-time starter grant (reserved names only, idempotent) and
   * replies with a {@link CardStateMessage} the installer routes to {@link
   * CardBinderHandle.setState}. Fired on every open (the server dedupes the grant).
   */
  onOpenBinder: () => void;
  /**
   * Save the whole desired set of named decks. The installer sends `SaveCardDecks`; the
   * server re-validates and echoes the authoritative state back. Only called with decks
   * the editor already proved legal + owned.
   */
  onSaveDecks: (decks: WireNamedDeck[]) => void;
  /**
   * Open one unopened pack (trading card game, PRD #493 Slice 4). The installer sends
   * an `OpenCardPack` command; the server rolls five seeded cards, banks them, and
   * replies with the reveal (for the flip modal) + fresh state. Optional so a Slice-3
   * caller (no opening flow) still constructs cleanly.
   */
  onOpenPack?: () => void;
  /**
   * Buy one pack for {@link PACK_PRICE_GOLD} gold. The installer sends a `BuyCardPack`
   * command; the server is authoritative on price, gold, and the reserved-only rule
   * (an unaffordable purchase gets a private refusal line), and echoes fresh state.
   */
  onBuyPack?: () => void;
  /** Confirm the account's permanent one-time starter grant. */
  onChooseStarter?: (recipeId: StarterRecipeId) => void;
  /** Start an ephemeral Carl match with the selected learner loaner. */
  onPracticeRecipe?: (recipeId: StarterRecipeId, opponentRecipeId?: StarterRecipeId) => void;
  /** Start a full-health Carl practice match with one authoritative saved-deck slot. */
  onPracticeDeck?: (deckIndex: number) => void;
  /** Put an owned card reference into the chat composer on Ctrl/Cmd+click. */
  onShareCard?: (card: { cardId: string; rarity: CardRarity; name: string }) => void;
  /** Publish an honor (0) or gold-wager listing with a private selected deck. */
  onCreateDuelListing?: (deckIndex: number, wager: number) => void;
  /** Withdraw this account's current listing. */
  onCancelDuelListing?: () => void;
  /** Accept a public listing using a private selected deck. */
  onAcceptDuelListing?: (listingId: string, deckIndex: number) => void;
  /** Start one unclaimed daily Carl matchup using an authoritative saved-deck slot. */
  onStartDailyChallenge?: (challengeId: string, deckIndex: number) => void;
}

/** The handle the installer keeps to open the gump and feed it server state. */
export interface CardBinderHandle {
  /** Show the gump (centring it on first open) and request fresh state. */
  open(): void;
  /** Hide the gump. A right-click / ✕ / Escape does this too. */
  close(): void;
  /** Whether the gump is currently shown. */
  isOpen(): boolean;
  /** Feed the authoritative server {@link CardStateMessage} state and re-render. */
  setState(state: CardBinderState): void;
  /** Feed the server's current public duel-board projection and repaint that tab. */
  setDuelListings(state: CardDuelListingsState): void;
}

type BinderTab = 'collection' | 'decks' | 'challenges' | 'duels' | 'packs' | 'rules';

/** How many mana-cost buckets the curve bar shows: 1..6, then a `7+` bucket. */
const CURVE_BUCKETS = 7;

/**
 * The mana curve of a deck: card counts bucketed by cost, index `0..6` meaning cost
 * `1..6` and `7+` (the tail folds into the last bucket so the bar stays readable).
 * Pure and exported for a direct unit test.
 */
export function manaCurve(costs: readonly number[]): number[] {
  const buckets = new Array<number>(CURVE_BUCKETS).fill(0);
  for (const cost of costs) {
    const idx = Math.min(CURVE_BUCKETS - 1, Math.max(0, cost - 1));
    buckets[idx] = (buckets[idx] ?? 0) + 1;
  }
  return buckets;
}

/** The mana cost of a concrete deck ref (via the pure card definition), 0 for an unknown id. */
function refCost(ref: WireDeckCard): number {
  return safeCardDef(ref.cardId, ref.rarity)?.cost ?? 0;
}

/** Build the gump's tab strip + panes (same ids the CSS binds to). */
function ensureContent(doc: Document, root: HTMLElement): void {
  if (doc.getElementById('card-binder-body')) return;
  const tabs = doc.createElement('div');
  tabs.id = 'card-binder-tabs';
  for (const [id, label, pressed] of [
    ['card-binder-tab-collection', 'Cards', 'true'],
    ['card-binder-tab-decks', 'Decks', 'false'],
    ['card-binder-tab-packs', 'Packs', 'false'],
    ['card-binder-tab-challenges', 'Challenges', 'false'],
    ['card-binder-tab-duels', 'Duel Board', 'false'],
    ['card-binder-tab-rules', 'How to Play', 'false'],
  ] as const) {
    const b = doc.createElement('button');
    b.id = id;
    b.type = 'button';
    b.setAttribute('aria-pressed', pressed);
    b.textContent = label;
    tabs.appendChild(b);
  }

  const body = doc.createElement('div');
  body.id = 'card-binder-body';

  // Collection pane.
  const collection = doc.createElement('section');
  collection.id = 'card-binder-collection';
  const recipes = doc.createElement('section');
  recipes.id = 'card-binder-recipes';
  recipes.hidden = true;
  recipes.setAttribute('aria-label', 'Choose your starter deck');
  const collectionMain = doc.createElement('div');
  collectionMain.id = 'card-binder-collection-main';
  const reserve = doc.createElement('p');
  reserve.id = 'card-binder-reserve';
  reserve.hidden = true;
  const filter = doc.createElement('input');
  filter.id = 'card-binder-filter';
  filter.type = 'text';
  filter.placeholder = 'Filter by name…';
  filter.setAttribute('aria-label', 'Filter cards by name');
  const rarityFilter = doc.createElement('select');
  rarityFilter.id = 'card-binder-filter-rarity';
  rarityFilter.setAttribute('aria-label', 'Filter cards by rarity');
  for (const [value, label] of [
    ['', 'All rarities'],
    ['common', 'Common'],
    ['uncommon', 'Uncommon'],
    ['rare', 'Rare'],
    ['legendary', 'Legendary'],
  ] as const) {
    const option = doc.createElement('option');
    option.value = value;
    option.textContent = label;
    rarityFilter.appendChild(option);
  }
  const typeFilter = doc.createElement('select');
  typeFilter.id = 'card-binder-filter-type';
  typeFilter.setAttribute('aria-label', 'Filter cards by type');
  for (const [value, label] of [
    ['', 'All types'],
    ['monster', 'Monsters'],
    ['spell', 'Spells'],
    ['item', 'Items'],
  ] as const) {
    const option = doc.createElement('option');
    option.value = value;
    option.textContent = label;
    typeFilter.appendChild(option);
  }
  const manaFilter = doc.createElement('select');
  manaFilter.id = 'card-binder-filter-mana';
  manaFilter.setAttribute('aria-label', 'Filter cards by mana cost');
  for (const [value, label] of [
    ['', 'All mana'],
    ['1', '1 mana'],
    ['2', '2 mana'],
    ['3', '3 mana'],
    ['4', '4 mana'],
    ['5', '5 mana'],
    ['6', '6 mana'],
    ['7', '7+ mana'],
  ] as const) {
    const option = doc.createElement('option');
    option.value = value;
    option.textContent = label;
    manaFilter.appendChild(option);
  }
  const clearFilters = doc.createElement('button');
  clearFilters.id = 'card-binder-filter-clear';
  clearFilters.type = 'button';
  clearFilters.textContent = 'Clear';
  const filterSummary = doc.createElement('span');
  filterSummary.id = 'card-binder-filter-summary';
  filterSummary.setAttribute('aria-live', 'polite');
  const collectionFilters = doc.createElement('div');
  collectionFilters.id = 'card-binder-filters';
  collectionFilters.append(filter, rarityFilter, typeFilter, manaFilter, clearFilters, filterSummary);
  const grid = doc.createElement('div');
  grid.id = 'card-binder-collection-grid';
  grid.className = 'card-grid';
  const empty = doc.createElement('p');
  empty.id = 'card-binder-collection-empty';
  empty.hidden = true;
  collectionMain.append(reserve, collectionFilters, grid, empty);
  collection.append(recipes, collectionMain);

  // Decks pane (overview list + editor, toggled by hidden).
  const decks = doc.createElement('section');
  decks.id = 'card-binder-decks';
  decks.hidden = true;
  const overview = doc.createElement('div');
  overview.id = 'card-binder-deck-overview';
  const overviewHeader = doc.createElement('header');
  overviewHeader.id = 'card-binder-deck-overview-header';
  const overviewTitle = doc.createElement('div');
  const overviewEyebrow = doc.createElement('span');
  overviewEyebrow.className = 'card-binder-deck-eyebrow';
  overviewEyebrow.textContent = 'Deck arsenal';
  const overviewHeading = doc.createElement('h3');
  overviewHeading.textContent = 'Your decks';
  const overviewCopy = doc.createElement('p');
  overviewCopy.textContent = `Build up to ${MAX_NAMED_DECKS} decks of ${DECK_SIZE} cards.`;
  overviewTitle.append(overviewEyebrow, overviewHeading, overviewCopy);
  const overviewSlots = doc.createElement('span');
  overviewSlots.id = 'card-binder-deck-slots';
  overviewSlots.setAttribute('aria-live', 'polite');
  overviewHeader.append(overviewTitle, overviewSlots);
  const deckList = doc.createElement('ul');
  deckList.id = 'card-binder-deck-list';
  const newBtn = doc.createElement('button');
  newBtn.id = 'card-binder-deck-new';
  newBtn.type = 'button';
  // Keep the control in the initial DOM so the binder's fail-closed element
  // validation can adopt it. The first overview render moves it into the visual
  // empty folio slot.
  newBtn.hidden = true;
  overview.append(overviewHeader, deckList, newBtn);

  const editor = doc.createElement('div');
  editor.id = 'card-binder-deck-editor';
  editor.hidden = true;
  const editorWorkspace = doc.createElement('div');
  editorWorkspace.id = 'card-binder-editor-workspace';
  const editorMain = doc.createElement('div');
  editorMain.id = 'card-binder-editor-main';
  const nameInput = doc.createElement('input');
  nameInput.id = 'card-binder-deck-name';
  nameInput.type = 'text';
  nameInput.placeholder = 'Deck name';
  nameInput.setAttribute('aria-label', 'Deck name');
  const filterRow = doc.createElement('div');
  filterRow.id = 'card-binder-editor-filter-row';
  const editorFilter = doc.createElement('input');
  editorFilter.id = 'card-binder-editor-filter';
  editorFilter.type = 'text';
  editorFilter.placeholder = 'Filter by name…';
  editorFilter.setAttribute('aria-label', 'Filter collection cards by name');
  const editorRarityFilter = rarityFilter.cloneNode(true) as HTMLSelectElement;
  editorRarityFilter.id = 'card-binder-editor-filter-rarity';
  editorRarityFilter.setAttribute('aria-label', 'Filter deck-building cards by rarity');
  const editorTypeFilter = typeFilter.cloneNode(true) as HTMLSelectElement;
  editorTypeFilter.id = 'card-binder-editor-filter-type';
  editorTypeFilter.setAttribute('aria-label', 'Filter deck-building cards by type');
  const editorManaFilter = manaFilter.cloneNode(true) as HTMLSelectElement;
  editorManaFilter.id = 'card-binder-editor-filter-mana';
  editorManaFilter.setAttribute('aria-label', 'Filter deck-building cards by mana cost');
  const editorFilterClear = doc.createElement('button');
  editorFilterClear.id = 'card-binder-editor-filter-clear';
  editorFilterClear.type = 'button';
  editorFilterClear.textContent = 'Clear';
  const editorFilterSummary = doc.createElement('span');
  editorFilterSummary.id = 'card-binder-editor-filter-summary';
  editorFilterSummary.setAttribute('aria-live', 'polite');
  filterRow.append(
    editorFilter,
    editorRarityFilter,
    editorTypeFilter,
    editorManaFilter,
    editorFilterClear,
    editorFilterSummary,
  );
  const editorCollection = doc.createElement('div');
  editorCollection.id = 'card-binder-editor-collection';
  editorCollection.className = 'card-grid';
  const editorEmpty = doc.createElement('p');
  editorEmpty.id = 'card-binder-editor-empty';
  editorEmpty.hidden = true;
  editorMain.append(filterRow, editorCollection, editorEmpty);

  const currentDeck = doc.createElement('aside');
  currentDeck.id = 'card-binder-current-deck';
  const currentDeckHeading = doc.createElement('h3');
  currentDeckHeading.textContent = 'Current Deck';
  const currentDeckHeader = doc.createElement('div');
  currentDeckHeader.id = 'card-binder-current-deck-header';
  const currentDeckCount = doc.createElement('span');
  currentDeckCount.id = 'card-binder-current-deck-count';
  currentDeckHeader.append(nameInput, currentDeckCount);
  const currentDeckList = doc.createElement('ol');
  currentDeckList.id = 'card-binder-current-deck-list';
  const curve = doc.createElement('div');
  curve.id = 'card-binder-deck-curve';
  curve.setAttribute('aria-label', 'Mana curve');
  const editorButtons = doc.createElement('div');
  editorButtons.id = 'card-binder-editor-buttons';
  const saveBtn = doc.createElement('button');
  saveBtn.id = 'card-binder-deck-save';
  saveBtn.type = 'button';
  saveBtn.textContent = 'Save deck';
  const backBtn = doc.createElement('button');
  backBtn.id = 'card-binder-deck-back';
  backBtn.type = 'button';
  backBtn.textContent = 'Back';
  editorButtons.append(saveBtn, backBtn);
  currentDeck.append(currentDeckHeading, currentDeckHeader, currentDeckList, curve, editorButtons);
  editorWorkspace.append(editorMain, currentDeck);
  editor.append(editorWorkspace);

  decks.append(overview, editor);

  // Daily Challenges pane: server-authored Carl matchups and account-backed progress.
  const challenges = doc.createElement('section');
  challenges.id = 'card-binder-challenges';
  challenges.hidden = true;
  const challengeOverview = doc.createElement('div');
  challengeOverview.id = 'card-binder-challenge-overview';
  const challengeHeader = doc.createElement('header');
  challengeHeader.id = 'card-binder-challenge-header';
  const challengeHeadingWrap = doc.createElement('div');
  const challengeEyebrow = doc.createElement('span');
  challengeEyebrow.className = 'card-binder-challenge-eyebrow';
  challengeEyebrow.textContent = 'Daily card battles';
  const challengeHeading = doc.createElement('h3');
  challengeHeading.textContent = 'Challenge Card Master Carl';
  const challengeLead = doc.createElement('p');
  challengeLead.textContent =
    'Face Carl at his full 100 health. Win each matchup once to earn gold without entering the Arena.';
  challengeHeadingWrap.append(challengeEyebrow, challengeHeading, challengeLead);
  const challengeProgress = doc.createElement('span');
  challengeProgress.id = 'card-binder-challenge-progress';
  challengeProgress.setAttribute('aria-live', 'polite');
  challengeHeader.append(challengeHeadingWrap, challengeProgress);

  const challengeToolbar = doc.createElement('div');
  challengeToolbar.id = 'card-binder-challenge-toolbar';
  const challengeDeckField = doc.createElement('label');
  challengeDeckField.className = 'card-binder-challenge-deck-field';
  const challengeDeckLabel = doc.createElement('span');
  challengeDeckLabel.textContent = 'Your deck';
  const challengeDeck = doc.createElement('select');
  challengeDeck.id = 'card-binder-challenge-deck';
  challengeDeck.setAttribute('aria-label', 'Deck for daily challenge');
  challengeDeckField.append(challengeDeckLabel, challengeDeck);
  const challengeReset = doc.createElement('span');
  challengeReset.id = 'card-binder-challenge-reset';
  challengeReset.textContent = 'New challenges arrive at 00:00 UTC';
  challengeToolbar.append(challengeDeckField, challengeReset);
  const challengeList = doc.createElement('div');
  challengeList.id = 'card-binder-challenge-list';
  challengeList.setAttribute('aria-label', 'Daily Card Master Carl challenges');
  challengeOverview.append(challengeHeader, challengeToolbar, challengeList);
  challenges.appendChild(challengeOverview);

  // Duel Board pane: the server owns membership and terms; this is only a public
  // projection plus create/cancel/accept intents. Deck choices never appear in rows.
  const duels = doc.createElement('section');
  duels.id = 'card-binder-duels';
  duels.hidden = true;
  const duelOverview = doc.createElement('div');
  duelOverview.id = 'card-binder-duel-overview';
  const duelHeader = doc.createElement('header');
  duelHeader.id = 'card-binder-duel-header';
  const duelHeadingWrap = doc.createElement('div');
  const duelEyebrow = doc.createElement('span');
  duelEyebrow.className = 'card-binder-duel-eyebrow';
  duelEyebrow.textContent = 'Player versus player';
  const duelHeading = doc.createElement('h3');
  duelHeading.textContent = 'Duel Board';
  const duelLead = doc.createElement('p');
  duelLead.textContent =
    'Challenge another player for honor or gold. Your chosen deck stays private until the cards hit the table.';
  duelHeadingWrap.append(duelEyebrow, duelHeading, duelLead);
  const duelCount = doc.createElement('span');
  duelCount.id = 'card-binder-duel-count';
  duelCount.setAttribute('aria-live', 'polite');
  duelHeader.append(duelHeadingWrap, duelCount);

  // The listing form disappears while the player's own challenge is on the board.
  // The live listing itself carries the cancel action beside its public terms.
  const duelPost = doc.createElement('div');
  duelPost.id = 'card-binder-duel-post';
  const duelPostTitle = doc.createElement('h4');
  duelPostTitle.id = 'card-binder-duel-post-title';
  duelPostTitle.textContent = 'Post your challenge';
  const duelForm = doc.createElement('div');
  duelForm.id = 'card-binder-duel-form';
  const duelDeckField = doc.createElement('label');
  duelDeckField.className = 'card-binder-duel-field';
  const duelDeckLabel = doc.createElement('span');
  duelDeckLabel.className = 'card-binder-duel-field-label';
  duelDeckLabel.textContent = 'Your deck';
  const duelDeck = doc.createElement('select');
  duelDeck.id = 'card-binder-duel-deck';
  duelDeck.setAttribute('aria-label', 'Deck for card duel');
  const duelDeckHint = doc.createElement('span');
  duelDeckHint.className = 'card-binder-duel-field-hint';
  duelDeckHint.textContent = 'Never shown to your opponent';
  duelDeckField.append(duelDeckLabel, duelDeck, duelDeckHint);
  const duelWagerField = doc.createElement('label');
  duelWagerField.className = 'card-binder-duel-field';
  const duelWagerLabel = doc.createElement('span');
  duelWagerLabel.className = 'card-binder-duel-field-label';
  duelWagerLabel.textContent = 'Gold stake per player';
  const duelWager = doc.createElement('input');
  duelWager.id = 'card-binder-duel-wager';
  duelWager.type = 'number';
  duelWager.min = '0';
  duelWager.step = '1';
  duelWager.value = '0';
  duelWager.setAttribute('aria-label', 'Gold stake per player; zero for honor');
  const duelWagerHint = doc.createElement('span');
  duelWagerHint.className = 'card-binder-duel-field-hint';
  duelWagerHint.textContent = 'Leave at 0 for an honor duel';
  duelWagerField.append(duelWagerLabel, duelWager, duelWagerHint);
  const duelAction = doc.createElement('div');
  duelAction.className = 'card-binder-duel-field card-binder-duel-action';
  const duelCreate = doc.createElement('button');
  duelCreate.id = 'card-binder-duel-create';
  duelCreate.type = 'button';
  duelCreate.textContent = 'List honor duel';
  duelAction.appendChild(duelCreate);
  duelForm.append(duelDeckField, duelWagerField, duelAction);
  const duelStatus = doc.createElement('p');
  duelStatus.id = 'card-binder-duel-status';
  duelStatus.setAttribute('aria-live', 'polite');
  duelPost.append(duelPostTitle, duelForm, duelStatus);

  const duelBoard = doc.createElement('div');
  duelBoard.id = 'card-binder-duel-board';
  const duelBoardTitle = doc.createElement('h4');
  duelBoardTitle.id = 'card-binder-duel-board-title';
  duelBoardTitle.textContent = 'Open challenges';
  const duelList = doc.createElement('ul');
  duelList.id = 'card-binder-duel-list';
  duelList.setAttribute('aria-label', 'Players looking for card duels');
  duelBoard.append(duelBoardTitle, duelList);
  duelOverview.append(duelHeader, duelPost, duelBoard);
  duels.appendChild(duelOverview);

  // Packs pane.
  const packs = doc.createElement('section');
  packs.id = 'card-binder-packs';
  packs.hidden = true;
  const packsCount = doc.createElement('p');
  packsCount.id = 'card-binder-packs-count';
  const packStage = doc.createElement('div');
  packStage.id = 'card-binder-pack-stage';
  const packStack = doc.createElement('div');
  packStack.id = 'card-binder-pack-stack';
  packStack.hidden = true;
  packStack.setAttribute('role', 'img');
  for (let index = 0; index < 3; index += 1) {
    const art = doc.createElement('img');
    art.className = 'card-binder-pack-art';
    art.alt = '';
    art.draggable = false;
    packStack.append(art);
  }
  const packCount = doc.createElement('span');
  packCount.id = 'card-binder-pack-count';
  packStack.append(packCount);
  packStage.append(packStack);
  const openBtn = doc.createElement('button');
  openBtn.id = 'card-binder-open-pack';
  openBtn.type = 'button';
  openBtn.textContent = 'Open a pack';
  const buyBtn = doc.createElement('button');
  buyBtn.id = 'card-binder-buy-packs';
  buyBtn.type = 'button';
  buyBtn.textContent = `Buy a card pack — ${PACK_PRICE_GOLD.toLocaleString('en-US')} gold`;
  // The pack-purchase disclosure (odds, rarity fairness, duplicate rebate) that the
  // retired vendor shop used to carry: it must stay beside the buy action.
  const buyDisclosure = doc.createElement('p');
  buyDisclosure.id = 'card-binder-buy-disclosure';
  buyDisclosure.textContent =
    `${PACK_RARITY_FAIRNESS_DISCLOSURE} ${PACK_ODDS_DISCLOSURE} ${PACK_DUPLICATE_DISCLOSURE}`;
  packs.append(packsCount, packStage, openBtn, buyBtn, buyDisclosure);

  // Rules pane. Its facts come from the sim constants above, while this module
  // only owns their readable presentation.
  const rules = doc.createElement('section');
  rules.id = 'card-binder-rules';
  rules.hidden = true;
  const title = doc.createElement('h3');
  title.textContent = 'Banksitter TCG Rules';
  const intro = doc.createElement('p');
  intro.className = 'card-binder-rules-lead';
  intro.textContent = `Reduce the enemy hero from ${HERO_START_HP} health to 0 before they do the same to you.`;
  rules.append(title, intro);

  const addSection = (heading: string, items: readonly string[]): void => {
    const section = doc.createElement('article');
    const h = doc.createElement('h4');
    h.textContent = heading;
    const list = doc.createElement('ul');
    for (const text of items) {
      const item = doc.createElement('li');
      item.textContent = text;
      list.appendChild(item);
    }
    section.append(h, list);
    rules.appendChild(section);
  };

  addSection('Build your deck', [
    `A legal deck contains exactly ${DECK_SIZE} cards.`,
    `You may include at most ${MAX_COPIES_PER_NAME} copies of a card name, across all rarities.`,
    'Rarity controls collectability and availability, not card power. Every printing of a card plays the same, and selected chase cards appear only as rare or legendary.',
  ]);
  addSection('Start the match', [
    `Both heroes begin at ${HERO_START_HP} health. The first player opens with ${DEFAULT_OPENING_HAND[0]} cards; the second opens with ${DEFAULT_OPENING_HAND[1]}.`,
    'Before turn one, each player may replace any number of opening cards once. Replacements are drawn before rejected cards return to the deck.',
    `The first turn starts with ${START_MANA} mana crystal. Your crystals increase by 1 on each of your turns, up to ${MAX_MANA}, and refill at turn start.`,
    `Both opening hands lock before the Coin is dealt. The second player also receives ${COIN_CARD_NAME}: a zero-cost card worth 1 extra mana on the turn it is played. It can be held for any later turn.`,
  ]);
  addSection('Take your turn', [
    'Draw a card, spend mana to play cards, attack with ready minions, then end your turn.',
    `You may control up to ${BOARD_SLOTS} minions and hold up to ${HAND_LIMIT} cards. A draw into a full hand is burned.`,
    'Monster cards stay on the board. They normally wait until your next turn to attack.',
    'Spell and item cards resolve their printed effect immediately instead of entering the board.',
    'When a minion attacks another minion, both deal their attack damage at the same time. A minion at 0 health is destroyed.',
    `You have ${TURN_TIMER_SECONDS} seconds. A timeout ends your turn; ${TIMEOUTS_TO_FORFEIT} consecutive timeouts forfeit the match.`,
  ]);
  addSection('Win the game', [
    'Attack the enemy hero when no Taunt minion blocks you. The first hero reduced to 0 health loses.',
    `If your deck is empty when you draw, your hero takes rising fatigue damage: ${FATIGUE_STEP}, then ${FATIGUE_STEP * 2}, then ${FATIGUE_STEP * 3}, and so on.`,
    'You may concede at any time.',
  ]);
  addSection('Keywords', CARD_KEYWORDS.map((keyword) =>
    `${keyword[0]!.toUpperCase()}${keyword.slice(1)} — ${cardKeywordText(keyword)}`,
  ));

  body.append(collection, decks, packs, challenges, duels, rules);
  root.append(tabs, body);
}

/**
 * Build (or adopt) the `#card-binder` gump. A body-less document degrades to a
 * harmless no-op handle so a bare client never throws.
 */
export function initCardBinder(deps: CardBinderDeps): CardBinderHandle {
  const doc = deps.doc ?? document;

  /** The latest authoritative server state; `null` until the first `CardState` arrives. */
  let state: CardBinderState | null = null;
  /** The current collection-tab name filter (lower-cased). */
  let filterText = '';
  /** Empty means every rarity; otherwise only the selected concrete rarity is shown. */
  let filterRarity: CardRarity | '' = '';
  /** Empty means every card kind; otherwise only the selected kind is shown. */
  let filterType: 'monster' | 'spell' | 'item' | '' = '';
  /** Empty means every mana cost; `7` is the open-ended 7+ bucket. */
  let filterMana: '' | '1' | '2' | '3' | '4' | '5' | '6' | '7' = '';
  /** The deck-editor's own collection-column name filter, independent of the Collection tab. */
  let editorFilterText = '';
  let editorFilterRarity: CardRarity | '' = '';
  let editorFilterType: 'monster' | 'spell' | 'item' | '' = '';
  let editorFilterMana: '' | '1' | '2' | '3' | '4' | '5' | '6' | '7' = '';
  /**
   * The deck being edited as a working copy of refs, or `null` in the overview. The
   * editor mutates this copy freely; only a Save commits it back to the server. The
   * slot index it maps to (`editIndex === decks.length` means a brand-new deck).
   */
  let editRefs: WireDeckCard[] | null = null;
  let editIndex = 0;
  let editName = '';
  let selectedRecipeId: StarterRecipeId = STARTER_RECIPES[0]!.id;
  let selectedChallengeDeckIndex: number | null = null;
  let selectedDuelDeckIndex: number | null = null;
  let duelListings: CardDuelListingsState = { listings: [] };

  const noop: CardBinderHandle = {
    open: () => {},
    close: () => {},
    isOpen: () => false,
    setState: () => {},
    setDuelListings: () => {},
  };

  const win = createUiWindow({
    doc,
    id: 'card-binder',
    title: 'Card Binder',
    ariaLabel: 'Card Binder',
    fallbackWidth: 1180,
    fallbackHeight: 780,
    centerVertically: true,
    escapeCloses: true,
    onOpen() {
      // Every open returns to the Collection tab, drops any half-finished edit, and
      // asks the server for fresh state (which also performs the one-time starter
      // grant). The reply arrives via setState and repaints.
      tabs.set('collection');
      editRefs = null;
      selectedChallengeDeckIndex = null;
      selectedDuelDeckIndex = null;
      filterText = '';
      filterRarity = '';
      filterType = '';
      filterMana = '';
      const filterEl = doc.getElementById('card-binder-filter') as HTMLInputElement | null;
      if (filterEl) filterEl.value = '';
      const rarityEl = doc.getElementById('card-binder-filter-rarity') as HTMLSelectElement | null;
      if (rarityEl) rarityEl.value = '';
      const typeEl = doc.getElementById('card-binder-filter-type') as HTMLSelectElement | null;
      if (typeEl) typeEl.value = '';
      const manaEl = doc.getElementById('card-binder-filter-mana') as HTMLSelectElement | null;
      if (manaEl) manaEl.value = '';
      render();
      deps.onOpenBinder();
    },
    onClose() {
      // However the window closes, a pending starter confirmation dies with it.
      confirmPrompt.hide();
    },
  });
  if (!win) return noop;
  ensureContent(doc, win.root);

  // The permanent-starter confirmation: a true centred alert modal (the wardrobe
  // purchase-gump pattern) instead of swapping buttons inline in the action bar.
  const confirmPrompt = createPromptCard(doc);
  confirmPrompt.root.classList.add('card-binder-confirm-gump');
  confirmPrompt.root.setAttribute('role', 'alertdialog');
  confirmPrompt.root.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    // Swallow the Escape so it dismisses only the dialog, not the binder behind it.
    event.stopPropagation();
    confirmPrompt.hide();
  });

  const confirmStarter = (recipeId: StarterRecipeId, recipeName: string): void => {
    const dialog = confirmPrompt.root;
    dialog.replaceChildren();
    dialog.setAttribute('aria-labelledby', 'card-binder-confirm-title');
    const title = doc.createElement('h3');
    title.id = 'card-binder-confirm-title';
    title.textContent = `Choose ${recipeName}?`;
    const copy = doc.createElement('p');
    copy.textContent = 'This choice is permanent and cannot be changed. You will receive all 20 cards.';
    const actions = doc.createElement('div');
    actions.className = 'card-binder-confirm-actions';
    const cancel = createPanelButton(doc, 'Keep comparing', 'plain');
    cancel.classList.add('card-binder-recipe-cancel');
    cancel.addEventListener('click', () => confirmPrompt.hide());
    const confirm = createPanelButton(doc, 'Confirm permanent starter', 'primary');
    confirm.classList.add('card-binder-recipe-confirm');
    confirm.addEventListener('click', () => {
      confirmPrompt.hide();
      deps.onChooseStarter?.(recipeId);
    });
    actions.append(cancel, confirm);
    dialog.append(title, copy, actions);
    confirmPrompt.show();
    // Focus the safe action: the choice is irreversible, so a stray Enter must
    // not commit it.
    cancel.focus();
  };

  const panes: Record<BinderTab, HTMLElement | null> = {
    collection: doc.getElementById('card-binder-collection'),
    decks: doc.getElementById('card-binder-decks'),
    challenges: doc.getElementById('card-binder-challenges'),
    duels: doc.getElementById('card-binder-duels'),
    packs: doc.getElementById('card-binder-packs'),
    rules: doc.getElementById('card-binder-rules'),
  };
  const decksTabEl = doc.getElementById('card-binder-tab-decks');
  const challengesTabEl = doc.getElementById('card-binder-tab-challenges');
  const packsTabEl = doc.getElementById('card-binder-tab-packs');
  const duelsTabEl = doc.getElementById('card-binder-tab-duels');
  const recipesEl = doc.getElementById('card-binder-recipes');
  const collectionMainEl = doc.getElementById('card-binder-collection-main');
  const reserveEl = doc.getElementById('card-binder-reserve');
  const filterEl = doc.getElementById('card-binder-filter') as HTMLInputElement | null;
  const rarityFilterEl = doc.getElementById('card-binder-filter-rarity') as HTMLSelectElement | null;
  const typeFilterEl = doc.getElementById('card-binder-filter-type') as HTMLSelectElement | null;
  const manaFilterEl = doc.getElementById('card-binder-filter-mana') as HTMLSelectElement | null;
  const clearFiltersEl = doc.getElementById('card-binder-filter-clear') as HTMLButtonElement | null;
  const filterSummaryEl = doc.getElementById('card-binder-filter-summary');
  const collectionGrid = doc.getElementById('card-binder-collection-grid');
  const collectionEmpty = doc.getElementById('card-binder-collection-empty');
  const currentDeckEl = doc.getElementById('card-binder-current-deck');
  const currentDeckCountEl = doc.getElementById('card-binder-current-deck-count');
  const currentDeckListEl = doc.getElementById('card-binder-current-deck-list');
  const overviewEl = doc.getElementById('card-binder-deck-overview');
  const deckSlotsEl = doc.getElementById('card-binder-deck-slots');
  const deckListEl = doc.getElementById('card-binder-deck-list');
  const newDeckBtn = doc.getElementById('card-binder-deck-new') as HTMLButtonElement | null;
  const editorEl = doc.getElementById('card-binder-deck-editor');
  const deckNameInput = doc.getElementById('card-binder-deck-name') as HTMLInputElement | null;
  const editorFilterEl = doc.getElementById('card-binder-editor-filter') as HTMLInputElement | null;
  const editorRarityFilterEl = doc.getElementById('card-binder-editor-filter-rarity') as HTMLSelectElement | null;
  const editorTypeFilterEl = doc.getElementById('card-binder-editor-filter-type') as HTMLSelectElement | null;
  const editorManaFilterEl = doc.getElementById('card-binder-editor-filter-mana') as HTMLSelectElement | null;
  const editorFilterClearEl = doc.getElementById('card-binder-editor-filter-clear') as HTMLButtonElement | null;
  const editorFilterSummaryEl = doc.getElementById('card-binder-editor-filter-summary');
  const editorEmptyEl = doc.getElementById('card-binder-editor-empty');
  const curveEl = doc.getElementById('card-binder-deck-curve');
  const editorCollectionEl = doc.getElementById('card-binder-editor-collection');
  const saveBtn = doc.getElementById('card-binder-deck-save') as HTMLButtonElement | null;
  const backBtn = doc.getElementById('card-binder-deck-back') as HTMLButtonElement | null;
  const packsCountEl = doc.getElementById('card-binder-packs-count');
  const packStackEl = doc.getElementById('card-binder-pack-stack');
  const packCountEl = doc.getElementById('card-binder-pack-count');
  const packArtEls = Array.from(
    doc.querySelectorAll<HTMLImageElement>('#card-binder-pack-stack .card-binder-pack-art'),
  );
  const openPackBtn = doc.getElementById('card-binder-open-pack') as HTMLButtonElement | null;
  const buyPacksBtn = doc.getElementById('card-binder-buy-packs') as HTMLButtonElement | null;
  const buyDisclosureEl = doc.getElementById('card-binder-buy-disclosure');
  const duelDeckEl = doc.getElementById('card-binder-duel-deck') as HTMLSelectElement | null;
  const duelWagerEl = doc.getElementById('card-binder-duel-wager') as HTMLInputElement | null;
  const duelCreateEl = doc.getElementById('card-binder-duel-create') as HTMLButtonElement | null;
  const duelStatusEl = doc.getElementById('card-binder-duel-status');
  const duelListEl = doc.getElementById('card-binder-duel-list');
  const duelCountEl = doc.getElementById('card-binder-duel-count');
  const duelPostEl = doc.getElementById('card-binder-duel-post');
  const duelPostTitleEl = doc.getElementById('card-binder-duel-post-title');
  const duelBoardTitleEl = doc.getElementById('card-binder-duel-board-title');
  const challengeDeckEl = doc.getElementById('card-binder-challenge-deck') as HTMLSelectElement | null;
  const challengeProgressEl = doc.getElementById('card-binder-challenge-progress');
  const challengeListEl = doc.getElementById('card-binder-challenge-list');
  if (
    !decksTabEl || !challengesTabEl || !duelsTabEl || !packsTabEl || !recipesEl || !collectionMainEl ||
    !reserveEl || !filterEl || !rarityFilterEl || !typeFilterEl || !manaFilterEl ||
    !clearFiltersEl || !filterSummaryEl || !collectionGrid || !collectionEmpty || !currentDeckEl ||
    !currentDeckCountEl || !currentDeckListEl || !overviewEl || !deckSlotsEl ||
    !deckListEl || !newDeckBtn || !editorEl || !deckNameInput || !editorFilterEl ||
    !editorRarityFilterEl || !editorTypeFilterEl || !editorManaFilterEl ||
    !editorFilterClearEl || !editorFilterSummaryEl || !editorEmptyEl ||
    !curveEl || editorCollectionEl === null ||
    !saveBtn || !backBtn ||
    !packsCountEl || !packStackEl || !packCountEl ||
    packArtEls.length !== 3 || !openPackBtn || !buyPacksBtn || !buyDisclosureEl ||
    !duelDeckEl || !duelWagerEl || !duelCreateEl || !duelStatusEl || !duelListEl ||
    !duelCountEl || !duelPostEl || !duelPostTitleEl || !duelBoardTitleEl ||
    !challengeDeckEl || !challengeProgressEl || !challengeListEl
  ) {
    return noop;
  }

  const tabs: TabsHandle<BinderTab> = createTabs<BinderTab>(
    [
      { button: doc.getElementById('card-binder-tab-collection'), value: 'collection' },
      { button: doc.getElementById('card-binder-tab-decks'), value: 'decks' },
      { button: doc.getElementById('card-binder-tab-packs'), value: 'packs' },
      { button: doc.getElementById('card-binder-tab-challenges'), value: 'challenges' },
      { button: doc.getElementById('card-binder-tab-duels'), value: 'duels' },
      { button: doc.getElementById('card-binder-tab-rules'), value: 'rules' },
    ],
    'collection',
    () => render(),
  );

  // --- Collection tab --------------------------------------------------------

  const wireShareGesture = (
    face: HTMLElement,
    entry: WireCardEntry,
    name: string,
    primaryActionLabel?: string,
  ): void => {
    if (!deps.onShareCard) return;
    const share = (): void => {
      deps.onShareCard?.({
        cardId: entry.cardId,
        rarity: entry.rarity as CardRarity,
        name,
      });
    };
    face.title = 'Ctrl/Cmd+click or Ctrl/Cmd+Enter to share this card in chat';
    face.tabIndex = 0;
    face.setAttribute('role', 'button');
    face.setAttribute(
      'aria-label',
      primaryActionLabel
        ? `${primaryActionLabel}. Control or Command plus Enter shares ${name} in chat.`
        : `Share ${name} in chat. Press Enter to share.`,
    );
    face.setAttribute(
      'aria-keyshortcuts',
      'Control+Enter Meta+Enter Control+Space Meta+Space',
    );
    face.addEventListener('click', (event) => {
      if (!event.ctrlKey && !event.metaKey) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      share();
    });
    face.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter' && event.key !== ' ') return;
      const modified = event.ctrlKey || event.metaKey;
      if (!modified && primaryActionLabel) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      share();
    });
  };

  /** The owned collection, filtered by the active controls, each cell carrying its count. */
  const renderCollectionGrid = (grid: HTMLElement, onClick?: (entry: WireCardEntry) => void): number => {
    grid.replaceChildren();
    const owned = state?.collection ?? [];
    const filtered = owned
      .filter((e) => {
        const def = safeCardDef(e.cardId, e.rarity);
        return def !== null &&
          def.name.toLowerCase().includes(filterText) &&
          (filterRarity === '' || e.rarity === filterRarity) &&
          (filterType === '' || def.kind === filterType) &&
          (filterMana === '' || (filterMana === '7' ? def.cost >= 7 : def.cost === Number(filterMana)));
      })
      .sort((a, b) => refCost(a) - refCost(b));
    for (const entry of filtered) {
      const def = safeCardDef(entry.cardId, entry.rarity);
      if (!def) continue;
      const face = renderCardFace(doc, cardFaceModel(def, entry.count));
      wireShareGesture(face, entry, def.name);
      if (onClick) {
        face.addEventListener('click', () => onClick(entry));
      }
      grid.appendChild(face);
    }
    return filtered.length;
  };

  const renderCollectionTab = (): void => {
    const showStarterOnboarding = state !== null &&
      (!state.reserved || state.starterEligible === true);
    recipesEl.hidden = !showStarterOnboarding;
    collectionMainEl.hidden = showStarterOnboarding;
    if (showStarterOnboarding) {
      renderRecipesTab();
      return;
    }

    const reserved = state?.reserved ?? true;
    reserveEl.textContent = RESERVE_TEXT;
    reserveEl.hidden = reserved;
    doc.getElementById('card-binder-filters')?.toggleAttribute('hidden', !reserved);
    collectionGrid.hidden = !reserved;
    if (!reserved) {
      collectionGrid.replaceChildren();
      collectionEmpty.hidden = true;
      return;
    }
    const shown = renderCollectionGrid(collectionGrid);
    const total = state?.collection.length ?? 0;
    const filtersActive = filterText !== '' || filterRarity !== '' || filterType !== '' || filterMana !== '';
    filterSummaryEl.textContent = filtersActive ? `${shown} of ${total} cards` : `${total} ${total === 1 ? 'card' : 'cards'}`;
    clearFiltersEl.disabled = !filtersActive;
    collectionEmpty.textContent = total === 0 ? EMPTY_COLLECTION_TEXT : 'No cards match these filters.';
    collectionEmpty.hidden = shown !== 0;
  };

  // --- Decks tab -------------------------------------------------------------

  const renderDeckOverview = (): void => {
    overviewEl.hidden = false;
    editorEl.hidden = true;
    deckListEl.replaceChildren();
    const decks = state?.decks ?? [];
    for (const [i, deck] of decks.entries()) {
      const li = doc.createElement('li');
      li.className = 'card-binder-deck-folio';
      const definitions = deck.cards
        .map((ref) => safeCardDef(ref.cardId, ref.rarity))
        .filter((def): def is NonNullable<typeof def> => Boolean(def));
      const signature = definitions.filter((def, index, all) =>
        all.findIndex((candidate) => candidate.cardId === def.cardId) === index,
      ).slice(0, 3);
      const monsterCount = definitions.filter((def) => def.kind === 'monster').length;
      const spellCount = definitions.filter((def) => def.kind === 'spell').length;
      const itemCount = definitions.filter((def) => def.kind === 'item').length;
      const averageMana = definitions.length > 0
        ? definitions.reduce((total, def) => total + def.cost, 0) / definitions.length
        : 0;
      const legal = validateDeck(deck.cards).ok;
      const fullyOwned = deckWithinCollection(deck.cards, state?.collection ?? []);
      const isDefault = i === defaultNamedDeckIndex(decks);

      const edit = doc.createElement('button');
      edit.type = 'button';
      edit.className = 'deck-edit';
      edit.setAttribute('aria-label', `Edit ${deck.name}`);
      edit.addEventListener('click', () => beginEdit(i));

      const preview = doc.createElement('span');
      preview.className = 'deck-folio-preview';
      for (const [previewIndex, def] of signature.entries()) {
        const face = renderCardFace(doc, cardFaceModel(def));
        face.classList.add('deck-folio-card');
        face.style.setProperty('--deck-card-index', String(previewIndex));
        preview.appendChild(face);
      }
      while (preview.childElementCount < 3) {
        const placeholder = doc.createElement('span');
        placeholder.className = 'deck-folio-card deck-folio-card-placeholder';
        placeholder.style.setProperty('--deck-card-index', String(preview.childElementCount));
        preview.appendChild(placeholder);
      }

      const details = doc.createElement('span');
      details.className = 'deck-folio-details';
      const number = doc.createElement('span');
      number.className = 'deck-folio-number';
      number.textContent = isDefault
        ? `Deck ${String(i + 1).padStart(2, '0')} · Default`
        : `Deck ${String(i + 1).padStart(2, '0')}`;
      const name = doc.createElement('span');
      name.className = 'deck-label';
      name.textContent = deck.name;
      const meta = doc.createElement('span');
      meta.className = 'deck-folio-meta';
      const status = doc.createElement('span');
      status.className = `deck-folio-status ${legal ? 'ready' : 'incomplete'}`;
      status.textContent = legal ? 'Ready' : 'Incomplete';
      const count = doc.createElement('span');
      count.className = 'deck-folio-count';
      count.textContent = `${deck.cards.length} / ${DECK_SIZE} cards`;
      meta.append(status, count);
      details.append(number, name, meta);

      const summary = doc.createElement('span');
      summary.className = 'deck-folio-summary';
      const composition = doc.createElement('span');
      composition.className = 'deck-folio-composition';
      for (const [value, label] of [
        [monsterCount, 'Monsters'],
        [spellCount, 'Spells'],
        [itemCount, 'Items'],
      ] as const) {
        const stat = doc.createElement('span');
        const statValue = doc.createElement('strong');
        statValue.textContent = String(value);
        const statLabel = doc.createElement('small');
        statLabel.textContent = label;
        stat.append(statValue, statLabel);
        composition.appendChild(stat);
      }
      const mana = doc.createElement('span');
      mana.className = 'deck-folio-mana';
      mana.textContent = `Average mana ${averageMana.toFixed(1)}`;
      summary.append(composition, mana);
      edit.append(preview, details, summary);

      const del = doc.createElement('button');
      del.type = 'button';
      del.className = 'deck-delete';
      del.textContent = 'Delete';
      del.setAttribute('aria-label', `Delete ${deck.name}`);
      del.addEventListener('click', () => deleteDeck(i));
      const practice = doc.createElement('button');
      practice.type = 'button';
      practice.className = 'deck-practice';
      practice.textContent = 'Practice';
      practice.setAttribute('aria-label', `Practice ${deck.name} against Card Master Carl`);
      practice.disabled = !legal || !fullyOwned || deps.onPracticeDeck === undefined;
      practice.title = !legal || !fullyOwned
        ? 'Practice requires a legal, fully owned deck.'
        : '';
      practice.addEventListener('click', () => deps.onPracticeDeck?.(i));
      const makeDefault = doc.createElement('button');
      makeDefault.type = 'button';
      makeDefault.className = 'deck-default';
      makeDefault.textContent = isDefault ? 'Default deck' : 'Set default';
      makeDefault.setAttribute('aria-label', isDefault
        ? `${deck.name} is your default deck`
        : `Set ${deck.name} as your default deck`);
      makeDefault.setAttribute('aria-pressed', String(isDefault));
      makeDefault.disabled = isDefault || !legal || !fullyOwned;
      makeDefault.title = !legal || !fullyOwned
        ? 'Only a legal, fully owned deck can be the default.'
        : '';
      makeDefault.addEventListener('click', () => setDefaultDeck(i));
      li.classList.toggle('default', isDefault);
      li.append(edit, practice, makeDefault, del);
      deckListEl.appendChild(li);
    }
    const reserved = state?.reserved ?? false;
    newDeckBtn.disabled = !reserved || decks.length >= MAX_NAMED_DECKS;
    newDeckBtn.title = !reserved
      ? RESERVE_TEXT
      : decks.length >= MAX_NAMED_DECKS
        ? `You can keep at most ${MAX_NAMED_DECKS} decks.`
        : '';
    deckSlotsEl.textContent = `${decks.length} / ${MAX_NAMED_DECKS} deck slots`;
    const newSlot = doc.createElement('li');
    newSlot.className = 'card-binder-deck-folio card-binder-deck-folio-new';
    newSlot.hidden = decks.length >= MAX_NAMED_DECKS;
    newDeckBtn.hidden = false;
    const newMark = doc.createElement('span');
    newMark.className = 'deck-folio-new-mark';
    newMark.setAttribute('aria-hidden', 'true');
    newMark.textContent = '+';
    const newCopy = doc.createElement('span');
    const newLabel = doc.createElement('strong');
    newLabel.textContent = decks.length >= MAX_NAMED_DECKS ? 'All folios bound' : 'Forge a new deck';
    const newHint = doc.createElement('small');
    newHint.textContent = decks.length >= MAX_NAMED_DECKS
      ? `Delete a deck to free one of your ${MAX_NAMED_DECKS} slots.`
      : `${MAX_NAMED_DECKS - decks.length} ${MAX_NAMED_DECKS - decks.length === 1 ? 'slot remains' : 'slots remain'}`;
    newCopy.append(newLabel, newHint);
    newDeckBtn.replaceChildren(newMark, newCopy);
    newSlot.appendChild(newDeckBtn);
    deckListEl.appendChild(newSlot);
  };

  /** Whether a specific owned card can be added to the working deck (legality + ownership). */
  const canAddEntry = (entry: WireCardEntry): boolean => {
    if (!editRefs) return false;
    if (!canAddToDeck(editRefs, entry.cardId)) return false;
    const inDeck = editRefs.filter((r) => r.cardId === entry.cardId && r.rarity === entry.rarity).length;
    return inDeck < collectionCount(state?.collection ?? [], entry.cardId, entry.rarity);
  };

  const renderEditor = (): void => {
    if (!editRefs) return;
    overviewEl.hidden = true;
    editorEl.hidden = false;
    deckNameInput.value = editName;

    // The collection side: every owned card, greyed when it can't be added.
    editorCollectionEl.replaceChildren();
    const collection = state?.collection ?? [];
    const filteredCollection = collection
      .filter((entry) => {
        const def = safeCardDef(entry.cardId, entry.rarity);
        return def !== null &&
          def.name.toLowerCase().includes(editorFilterText) &&
          (editorFilterRarity === '' || entry.rarity === editorFilterRarity) &&
          (editorFilterType === '' || def.kind === editorFilterType) &&
          (editorFilterMana === '' ||
            (editorFilterMana === '7' ? def.cost >= 7 : def.cost === Number(editorFilterMana)));
      })
      .sort((a, b) => refCost(a) - refCost(b));
    for (const entry of filteredCollection) {
      const def = safeCardDef(entry.cardId, entry.rarity);
      if (!def) continue;
      const face = renderCardFace(doc, cardFaceModel(def, entry.count));
      const addable = canAddEntry(entry);
      wireShareGesture(face, entry, def.name, addable ? `Add ${def.name} to deck` : undefined);
      face.classList.toggle('card-add-disabled', !addable && Boolean(deps.onShareCard));
      face.classList.toggle('card-disabled', !addable && !deps.onShareCard);
      face.setAttribute('aria-disabled', String(!addable && !deps.onShareCard));
      if (addable) {
        face.setAttribute('role', 'button');
        face.tabIndex = 0;
        if (!deps.onShareCard) face.setAttribute('aria-label', `Add ${def.name} to deck`);
        face.addEventListener('click', () => addCard(entry));
        face.addEventListener('keydown', (event) => {
          if (event.key === 'Enter' || event.key === ' ') addCard(entry);
        });
      }
      editorCollectionEl.appendChild(face);
    }
    const editorFiltersActive = editorFilterText !== '' || editorFilterRarity !== '' ||
      editorFilterType !== '' || editorFilterMana !== '';
    editorFilterSummaryEl.textContent = editorFiltersActive
      ? `${filteredCollection.length} of ${collection.length} cards`
      : `${collection.length} ${collection.length === 1 ? 'card' : 'cards'}`;
    editorFilterClearEl.disabled = !editorFiltersActive;
    editorEmptyEl.textContent = collection.length === 0 ? EMPTY_COLLECTION_TEXT : 'No cards match these filters.';
    editorEmptyEl.hidden = filteredCollection.length !== 0;

    // The deck side: compact rows with an explicit remove control.
    currentDeckListEl.replaceChildren();
    const sorted = [...editRefs].sort((a, b) => refCost(a) - refCost(b));
    for (const ref of sorted) {
      const def = safeCardDef(ref.cardId, ref.rarity);
      if (!def) continue;
      const row = doc.createElement('li');
      const cost = doc.createElement('span');
      cost.className = 'deck-card-cost';
      cost.textContent = String(def.cost);
      const name = doc.createElement('span');
      name.className = 'deck-card-name';
      name.textContent = def.name;
      const remove = doc.createElement('button');
      remove.type = 'button';
      remove.className = 'deck-card-remove';
      remove.textContent = '\u00d7';
      remove.setAttribute('aria-label', `Remove ${def.name} from deck`);
      remove.addEventListener('click', () => removeCard(ref));
      row.append(cost, name, remove);
      currentDeckListEl.appendChild(row);
    }

    // The mana curve bar + card count.
    const curve = manaCurve(editRefs.map(refCost));
    curveEl.replaceChildren();
    const max = Math.max(1, ...curve);
    for (const [i, n] of curve.entries()) {
      const col = doc.createElement('div');
      col.className = 'curve-col';
      const bar = doc.createElement('div');
      bar.className = 'curve-bar';
      bar.style.height = `${(n / max) * 100}%`;
      const label = doc.createElement('span');
      label.className = 'curve-label';
      label.textContent = i === CURVE_BUCKETS - 1 ? `${i + 1}+` : String(i + 1);
      const value = doc.createElement('span');
      value.className = 'curve-value';
      value.textContent = String(n);
      col.append(value, bar, label);
      curveEl.appendChild(col);
    }

    currentDeckCountEl.textContent = `${editRefs.length} / ${DECK_SIZE}`;
    const legal = validateDeck(editRefs.map((r) => ({ cardId: r.cardId, rarity: r.rarity as CardRarity }))).ok;
    const owned = deckWithinCollection(
      editRefs.map((r) => ({ cardId: r.cardId, rarity: r.rarity as CardRarity })),
      state?.collection ?? [],
    );
    saveBtn.disabled = !legal || !owned || editName.trim().length === 0;
    saveBtn.title = saveBtn.disabled
      ? `A deck must be exactly ${DECK_SIZE} owned cards (≤ 2 per name) with a name.`
      : '';
    currentDeckCountEl.classList.toggle('deck-legal', legal);
  };

  const beginEdit = (index: number): void => {
    const decks = state?.decks ?? [];
    editIndex = index;
    const existing = decks[index];
    editName = existing ? existing.name : '';
    editRefs = existing ? existing.cards.map((c) => ({ cardId: c.cardId, rarity: c.rarity })) : [];
    editorFilterText = '';
    editorFilterRarity = '';
    editorFilterType = '';
    editorFilterMana = '';
    editorFilterEl.value = '';
    editorRarityFilterEl.value = '';
    editorTypeFilterEl.value = '';
    editorManaFilterEl.value = '';
    render();
  };

  const beginNew = (): void => {
    beginEdit(state?.decks.length ?? 0);
  };

  const addCard = (entry: WireCardEntry): void => {
    if (!editRefs || !canAddEntry(entry)) return;
    editRefs.push({ cardId: entry.cardId, rarity: entry.rarity });
    renderEditor();
  };

  const removeCard = (ref: WireDeckCard): void => {
    if (!editRefs) return;
    const i = editRefs.findIndex((r) => r.cardId === ref.cardId && r.rarity === ref.rarity);
    if (i >= 0) editRefs.splice(i, 1);
    renderEditor();
  };

  /** Commit the working deck into the desired set and push it to the server. */
  const saveDeck = (): void => {
    if (!editRefs) return;
    const legal = validateDeck(editRefs.map((r) => ({ cardId: r.cardId, rarity: r.rarity as CardRarity }))).ok;
    const owned = deckWithinCollection(
      editRefs.map((r) => ({ cardId: r.cardId, rarity: r.rarity as CardRarity })),
      state?.collection ?? [],
    );
    if (!legal || !owned || editName.trim().length === 0) return;
    const decks = (state?.decks ?? []).map((d) => ({
      name: d.name,
      cards: d.cards.map((c) => ({ ...c })),
      ...(d.default === true ? { default: true } : {}),
    }));
    const previous = decks[editIndex];
    const saved: WireNamedDeck = {
      name: editName.trim(),
      cards: editRefs.map((r) => ({ ...r })),
      ...(previous?.default === true ? { default: true } : {}),
    };
    if (editIndex < decks.length) decks[editIndex] = saved;
    else decks.push(saved);
    editRefs = null;
    deps.onSaveDecks(decks.slice(0, MAX_NAMED_DECKS));
    // The authoritative echo (setState) repaints; show the overview meanwhile.
    render();
  };

  const deleteDeck = (index: number): void => {
    const decks = (state?.decks ?? [])
      .filter((_, i) => i !== index)
      .map((d) => ({
        name: d.name,
        cards: d.cards.map((c) => ({ ...c })),
        ...(d.default === true ? { default: true } : {}),
      }));
    deps.onSaveDecks(decks);
    render();
  };

  const setDefaultDeck = (index: number): void => {
    const current = state?.decks ?? [];
    const deck = current[index];
    if (!deck || !validateDeck(deck.cards).ok || !deckWithinCollection(deck.cards, state?.collection ?? [])) return;
    const decks = current.map((candidate, candidateIndex) => ({
      name: candidate.name,
      cards: candidate.cards.map((card) => ({ ...card })),
      ...(candidateIndex === index ? { default: true } : {}),
    }));
    selectedChallengeDeckIndex = index;
    selectedDuelDeckIndex = index;
    deps.onSaveDecks(decks);
  };

  const renderDecksTab = (): void => {
    if (editRefs) renderEditor();
    else renderDeckOverview();
  };

  // --- Packs tab -------------------------------------------------------------

  const renderPacksTab = (): void => {
    const packs = state?.packs ?? 0;
    const reserved = state?.reserved ?? false;
    packsCountEl.textContent = reserved ? `Unopened packs: ${packs}` : RESERVE_TEXT;
    packsCountEl.hidden = reserved;

    const hasPacks = reserved && packs > 0;
    // Keep one pack on stage as the purchasable product when the player owns
    // none. Owned packs fan into a counted pile; the zero-count presentation
    // stays singular and carries no ownership badge.
    packStackEl.hidden = !reserved;
    packStackEl.classList.toggle('card-binder-pack-stack-purchase', reserved && !hasPacks);
    packCountEl.textContent = String(packs);
    packCountEl.hidden = !hasPacks;
    packStackEl.setAttribute(
      'aria-label',
      hasPacks
        ? `${packs} unopened card ${packs === 1 ? 'pack' : 'packs'}`
        : 'Card pack available to purchase',
    );
    if (reserved && win.isOpen() && tabs.value() === 'packs') {
      for (const art of packArtEls) {
        if (!art.hasAttribute('src')) art.src = cardPackUrl;
      }
    }
    // The matching actions are visible only for a reserved account (the
    // server re-checks authoritatively). An unwired action stays hidden.
    openPackBtn.hidden = !hasPacks || deps.onOpenPack === undefined;
    openPackBtn.disabled = !hasPacks;
    // Buying is always available to a reserved account (the server rules
    // affordability), so a player can stock up past their first pack.
    buyPacksBtn.hidden = !reserved || deps.onBuyPack === undefined;
    buyDisclosureEl.hidden = buyPacksBtn.hidden;
  };

  // --- Daily Challenges tab -------------------------------------------------

  const selectedChallengeDeck = (): number | undefined => {
    const index = Number(challengeDeckEl.value);
    return Number.isSafeInteger(index) && index >= 0 && index < (state?.decks.length ?? 0)
      ? index
      : undefined;
  };

  const renderChallengesTab = (): void => {
    const decks = state?.decks ?? [];
    challengeDeckEl.replaceChildren();
    for (const [index, deck] of decks.entries()) {
      const option = doc.createElement('option');
      option.value = String(index);
      option.textContent = deck.name;
      challengeDeckEl.appendChild(option);
    }
    const challengeIndex = selectedChallengeDeckIndex !== null && decks[selectedChallengeDeckIndex]
      ? selectedChallengeDeckIndex
      : defaultNamedDeckIndex(decks);
    if (challengeIndex !== undefined) challengeDeckEl.value = String(challengeIndex);
    challengeDeckEl.disabled = decks.length === 0;

    const earned = state?.dailyChallengeEarnedGold ?? 0;
    const limit = state?.dailyChallengeLimitGold ?? 3_000;
    challengeProgressEl.textContent =
      `${earned.toLocaleString('en-US')} / ${limit.toLocaleString('en-US')} gold earned`;
    challengeProgressEl.classList.toggle('complete', earned >= limit);

    challengeListEl.replaceChildren();
    const offers = state?.dailyChallenges ?? [];
    if (decks.length === 0) {
      const notice = doc.createElement('p');
      notice.className = 'card-binder-challenge-notice';
      notice.textContent = 'Save a legal deck first, then return here to challenge Carl.';
      challengeListEl.appendChild(notice);
    }
    for (const challenge of offers) {
      const row = doc.createElement('article');
      row.className = 'card-binder-challenge-row';
      row.dataset.challengeId = challenge.id;
      row.classList.toggle('completed', challenge.completed);

      const art = doc.createElement('img');
      art.className = 'card-binder-challenge-art';
      art.src = RECIPE_ART_URLS[challenge.opponentRecipeId];
      art.alt = '';
      art.draggable = false;

      const copy = doc.createElement('div');
      copy.className = 'card-binder-challenge-copy';
      const label = doc.createElement('span');
      label.className = 'card-binder-challenge-label';
      label.textContent = `${challenge.difficulty} | Card Master Carl`;
      const title = doc.createElement('h4');
      title.textContent = challenge.opponentName;
      const terms = doc.createElement('p');
      terms.textContent = 'Defeat Carl at 100 health. Losses are free to retry.';
      copy.append(label, title, terms);

      const reward = doc.createElement('div');
      reward.className = 'card-binder-challenge-reward';
      const amount = doc.createElement('strong');
      amount.textContent = `${challenge.rewardGold.toLocaleString('en-US')} gold`;
      const rewardLabel = doc.createElement('span');
      rewardLabel.textContent = challenge.completed ? 'Claimed today' : 'First win reward';
      reward.append(amount, rewardLabel);

      const start = doc.createElement('button');
      start.type = 'button';
      start.className = 'card-binder-challenge-start';
      start.textContent = challenge.completed ? 'Completed' : 'Challenge Carl';
      start.disabled = challenge.completed || decks.length === 0 || deps.onStartDailyChallenge === undefined;
      start.setAttribute('aria-label', challenge.completed
        ? `${challenge.opponentName} challenge completed`
        : `Challenge Card Master Carl with ${challenge.opponentName}`);
      start.addEventListener('click', () => {
        const deckIndex = selectedChallengeDeck();
        if (!challenge.completed && deckIndex !== undefined) {
          deps.onStartDailyChallenge?.(challenge.id, deckIndex);
        }
      });

      row.append(art, copy, reward, start);
      challengeListEl.appendChild(row);
    }
  };

  // --- Duel Board tab -------------------------------------------------------

  const selectedDuelDeck = (): number | undefined => {
    const index = Number(duelDeckEl.value);
    return Number.isSafeInteger(index) && index >= 0 && index < (state?.decks.length ?? 0)
      ? index
      : undefined;
  };

  const selectedDuelWager = (): number | undefined => {
    const wager = Number(duelWagerEl.value);
    return Number.isSafeInteger(wager) && wager >= 0 ? wager : undefined;
  };

  const renderDuelsTab = (): void => {
    const decks = state?.decks ?? [];
    duelDeckEl.replaceChildren();
    for (const [index, deck] of decks.entries()) {
      const option = doc.createElement('option');
      option.value = String(index);
      option.textContent = deck.name;
      duelDeckEl.appendChild(option);
    }
    const duelIndex = selectedDuelDeckIndex !== null && decks[selectedDuelDeckIndex]
      ? selectedDuelDeckIndex
      : defaultNamedDeckIndex(decks);
    if (duelIndex !== undefined) duelDeckEl.value = String(duelIndex);

    const listed = duelListings.yourListingId !== undefined;
    const canChooseDeck = decks.length > 0 && !listed;
    duelDeckEl.disabled = !canChooseDeck;
    duelWagerEl.disabled = listed;
    duelCreateEl.disabled = !canChooseDeck || selectedDuelWager() === undefined;
    duelPostEl.hidden = listed;
    duelPostTitleEl.textContent = 'Post your challenge';
    duelStatusEl.textContent = decks.length === 0
      ? 'Save a legal deck first - build one on the Decks tab, then return to challenge.'
      : 'Publish your terms, or accept an open challenge below.';

    const publicListings = duelListings.listings;
    duelCountEl.textContent =
      `${publicListings.length} open ${publicListings.length === 1 ? 'challenge' : 'challenges'}`;
    duelCountEl.classList.toggle('card-binder-duel-count-live', publicListings.length > 0);

    duelListEl.replaceChildren();
    if (publicListings.length === 0) {
      const empty = doc.createElement('li');
      empty.className = 'card-binder-duel-empty';
      const emptyMark = doc.createElement('span');
      emptyMark.className = 'card-binder-duel-empty-mark';
      emptyMark.setAttribute('aria-hidden', 'true');
      emptyMark.textContent = '⚔';
      const emptyTitle = doc.createElement('strong');
      emptyTitle.textContent = 'The board is empty';
      const emptyCopy = doc.createElement('span');
      emptyCopy.textContent =
        'No other players are listed yet. Post a challenge and be the first name on the board.';
      empty.append(emptyMark, emptyTitle, emptyCopy);
      duelListEl.appendChild(empty);
      return;
    }
    for (const listing of publicListings) {
      const isOwnListing = listing.id === duelListings.yourListingId;
      const row = doc.createElement('li');
      row.className = 'card-binder-duel-row';
      row.classList.toggle('card-binder-duel-row-own', isOwnListing);
      row.dataset.listingId = listing.id;
      const who = doc.createElement('span');
      who.className = 'card-binder-duel-player';
      const name = doc.createElement('span');
      name.className = 'card-binder-duel-player-name';
      const player = doc.createElement('strong');
      player.textContent = listing.playerName;
      name.appendChild(player);
      if (isOwnListing) {
        const yours = doc.createElement('span');
        yours.className = 'card-binder-duel-yours';
        yours.textContent = 'You';
        name.appendChild(yours);
      }
      const waited = doc.createElement('span');
      waited.className = 'card-binder-duel-wait';
      if (listing.createdAt !== undefined) {
        const minutes = Math.max(0, Math.floor((Date.now() - listing.createdAt) / 60_000));
        waited.textContent = minutes < 1 ? 'Listed just now' : `Listed ${minutes}m ago`;
      }
      who.append(name, waited);
      const terms = doc.createElement('span');
      terms.className = `card-binder-duel-terms ${listing.wager > 0 ? 'gold' : 'honor'}`;
      terms.textContent = listing.wager > 0
        ? `${listing.wager.toLocaleString('en-US')} gold each`
        : 'Honor duel';
      const action = doc.createElement('button');
      action.type = 'button';
      if (isOwnListing) {
        action.className = 'card-binder-duel-cancel';
        action.textContent = 'Cancel listing';
        action.setAttribute('aria-label', 'Cancel your card duel listing');
        action.addEventListener('click', () => deps.onCancelDuelListing?.());
      } else {
        action.className = 'card-binder-duel-accept';
        action.textContent = listing.wager > 0 ? 'Accept wager' : 'Accept duel';
        action.disabled = listed || selectedDuelDeck() === undefined;
        action.setAttribute(
          'aria-label',
          listing.wager > 0
            ? `Accept ${listing.playerName}'s ${listing.wager} gold card duel`
            : `Accept ${listing.playerName}'s honor card duel`,
        );
        action.addEventListener('click', () => {
          const deckIndex = selectedDuelDeck();
          if (deckIndex !== undefined && !listed) deps.onAcceptDuelListing?.(listing.id, deckIndex);
        });
      }
      row.append(who, terms, action);
      duelListEl.appendChild(row);
    }
  };

  const renderRecipesTab = (): void => {
    const pane = recipesEl;
    pane.replaceChildren();

    const chosenRecipe = STARTER_RECIPES.find((recipe) => recipe.id === state?.starterRecipe);
    const selectedRecipe = STARTER_RECIPES.find((recipe) => recipe.id === selectedRecipeId)
      ?? STARTER_RECIPES[0]!;

    const header = doc.createElement('header');
    header.className = 'card-binder-recipes-header';
    const headingWrap = doc.createElement('div');
    const eyebrow = doc.createElement('span');
    eyebrow.className = 'card-binder-recipes-eyebrow';
    eyebrow.textContent = chosenRecipe ? 'Starter collection' : 'New player choice';
    const heading = doc.createElement('h3');
    heading.textContent = chosenRecipe ? 'Explore the starter decks' : 'Choose your first deck';
    const lead = doc.createElement('p');
    lead.textContent = state?.reserved
      ? state.starterEligible
        ? 'Practice every deck for free. When you are ready, choose one permanently and receive all 20 cards.'
        : `${chosenRecipe?.name ?? 'Your starter'} is yours. You can still practice every other deck.`
      : 'Practice every deck for free. Reserve your name when you are ready to choose one permanently.';
    headingWrap.append(eyebrow, heading, lead);

    const status = doc.createElement('span');
    status.className = 'card-binder-recipes-status';
    status.textContent = state?.reserved
      ? state.starterEligible
        ? 'Starter choice available'
        : `Your starter: ${chosenRecipe?.name ?? 'Chosen'}`
      : 'Practice available';
    header.append(headingWrap, status);

    const gallery = doc.createElement('div');
    gallery.className = 'card-binder-recipe-gallery';
    gallery.setAttribute('aria-label', 'Starter deck choices');
    for (const recipe of STARTER_RECIPES) {
      const card = doc.createElement('button');
      card.type = 'button';
      card.className = 'card-binder-recipe-card';
      card.dataset.recipeId = recipe.id;
      const selected = recipe.id === selectedRecipe.id;
      card.classList.toggle('selected', selected);
      card.classList.toggle('chosen', recipe.id === chosenRecipe?.id);
      card.setAttribute('aria-pressed', String(selected));
      card.setAttribute('aria-label', `${recipe.name}, ${recipe.archetype}`);

      // The whole selector is the pack graphic: the wrap art names the deck and the
      // detail panel below reveals the actual cards, so the choice keeps a little
      // mystery. Only a Selected / Your starter tag overlays it.
      const art = doc.createElement('img');
      art.className = 'card-binder-recipe-art';
      art.src = RECIPE_ART_URLS[recipe.id];
      art.alt = '';
      art.draggable = false;
      card.append(art);
      if (recipe.id === chosenRecipe?.id || selected) {
        const badge = doc.createElement('span');
        badge.className = 'card-binder-recipe-badge';
        badge.textContent = recipe.id === chosenRecipe?.id ? 'Your starter' : 'Selected';
        card.append(badge);
      }
      card.addEventListener('click', () => {
        selectedRecipeId = recipe.id;
        renderRecipesTab();
      });
      gallery.appendChild(card);
    }

    const detail = doc.createElement('section');
    detail.className = 'card-binder-recipe-detail';
    detail.setAttribute('aria-label', `${selectedRecipe.name} deck details`);
    const detailHeading = doc.createElement('div');
    detailHeading.className = 'card-binder-recipe-detail-heading';
    const detailTitle = doc.createElement('h4');
    detailTitle.textContent = `${selectedRecipe.name} deck list`;
    const detailMeta = doc.createElement('span');
    detailMeta.textContent = `${selectedRecipe.cards.length} cards | ${selectedRecipe.cards.length / 2} unique`;
    detailHeading.append(detailTitle, detailMeta);

    // One mini card face per unique name (every recipe runs exactly 2 copies),
    // sorted by cost so the row reads like the deck's curve.
    const cardList = doc.createElement('ul');
    cardList.className = 'card-binder-recipe-list';
    const uniqueRefs: WireDeckCard[] = [];
    for (let index = 0; index < selectedRecipe.cards.length; index += 2) {
      uniqueRefs.push(selectedRecipe.cards[index]!);
    }
    uniqueRefs.sort((a, b) => refCost(a) - refCost(b));
    for (const ref of uniqueRefs) {
      const def = safeCardDef(ref.cardId, ref.rarity);
      const item = doc.createElement('li');
      if (def) {
        item.appendChild(renderCardFace(doc, cardFaceModel(def)));
      } else {
        const fallback = doc.createElement('span');
        fallback.textContent = ref.cardId;
        item.appendChild(fallback);
      }
      const copies = doc.createElement('span');
      copies.className = 'card-binder-recipe-copies';
      copies.textContent = 'x2';
      copies.setAttribute('aria-label', '2 copies');
      item.appendChild(copies);
      cardList.appendChild(item);
    }
    detail.append(detailHeading, cardList);

    const actions = doc.createElement('footer');
    actions.className = 'card-binder-recipe-actions';
    const actionCopy = doc.createElement('div');
    const actionTitle = doc.createElement('strong');
    actionTitle.textContent = selectedRecipe.name;
    const actionHint = doc.createElement('span');
    actionHint.textContent = 'Practice is free and never affects your permanent choice.';
    actionCopy.append(actionTitle, actionHint);

    const actionButtons = doc.createElement('div');
    const practice = doc.createElement('button');
    practice.type = 'button';
    practice.className = 'card-binder-recipe-practice';
    practice.textContent = `Practice ${selectedRecipe.name}`;
    practice.disabled = deps.onPracticeRecipe === undefined;
    practice.addEventListener('click', () => {
      // Carl always brings a random deck from his seeded rotation.
      deps.onPracticeRecipe?.(selectedRecipe.id);
    });
    actionButtons.appendChild(practice);

    if (state?.reserved && state.starterEligible && deps.onChooseStarter) {
      const choose = doc.createElement('button');
      choose.type = 'button';
      choose.className = 'card-binder-recipe-choose';
      choose.textContent = `Choose ${selectedRecipe.name}`;
      choose.setAttribute('aria-label', `Choose ${selectedRecipe.name} permanently`);
      choose.addEventListener('click', () => confirmStarter(selectedRecipe.id, selectedRecipe.name));
      actionButtons.appendChild(choose);
    }
    actions.append(actionCopy, actionButtons);
    pane.append(header, gallery, detail, actions);
  };

  // --- Shared ----------------------------------------------------------------

  const render = (): void => {
    const tab = tabs.value();
    const starterSelected = state?.reserved === true && state.starterRecipe != null;
    decksTabEl.hidden = !starterSelected;
    challengesTabEl.hidden = !starterSelected;
    duelsTabEl.hidden = !starterSelected;
    packsTabEl.hidden = !starterSelected;
    for (const key of ['collection', 'decks', 'challenges', 'duels', 'packs', 'rules'] as const) {
      const pane = panes[key];
      if (pane) pane.hidden = key !== tab;
    }
    if (tab === 'collection') renderCollectionTab();
    else if (tab === 'decks') renderDecksTab();
    else if (tab === 'challenges') renderChallengesTab();
    else if (tab === 'duels') renderDuelsTab();
    else if (tab === 'packs') renderPacksTab();
  };

  // --- Events ----------------------------------------------------------------

  filterEl.addEventListener('input', () => {
    filterText = filterEl.value.trim().toLowerCase();
    if (tabs.value() === 'collection') renderCollectionTab();
  });
  rarityFilterEl.addEventListener('change', () => {
    filterRarity = rarityFilterEl.value as CardRarity | '';
    if (tabs.value() === 'collection') renderCollectionTab();
  });
  typeFilterEl.addEventListener('change', () => {
    filterType = typeFilterEl.value as 'monster' | 'spell' | 'item' | '';
    if (tabs.value() === 'collection') renderCollectionTab();
  });
  manaFilterEl.addEventListener('change', () => {
    filterMana = manaFilterEl.value as typeof filterMana;
    if (tabs.value() === 'collection') renderCollectionTab();
  });
  clearFiltersEl.addEventListener('click', () => {
    filterText = '';
    filterRarity = '';
    filterType = '';
    filterMana = '';
    filterEl.value = '';
    rarityFilterEl.value = '';
    typeFilterEl.value = '';
    manaFilterEl.value = '';
    if (tabs.value() === 'collection') renderCollectionTab();
  });
  editorFilterEl.addEventListener('input', () => {
    editorFilterText = editorFilterEl.value.trim().toLowerCase();
    if (editRefs) renderEditor();
  });
  editorRarityFilterEl.addEventListener('change', () => {
    editorFilterRarity = editorRarityFilterEl.value as CardRarity | '';
    if (editRefs) renderEditor();
  });
  editorTypeFilterEl.addEventListener('change', () => {
    editorFilterType = editorTypeFilterEl.value as typeof editorFilterType;
    if (editRefs) renderEditor();
  });
  editorManaFilterEl.addEventListener('change', () => {
    editorFilterMana = editorManaFilterEl.value as typeof editorFilterMana;
    if (editRefs) renderEditor();
  });
  editorFilterClearEl.addEventListener('click', () => {
    editorFilterEl.value = '';
    editorRarityFilterEl.value = '';
    editorTypeFilterEl.value = '';
    editorManaFilterEl.value = '';
    editorFilterText = '';
    editorFilterRarity = '';
    editorFilterType = '';
    editorFilterMana = '';
    if (editRefs) renderEditor();
  });
  newDeckBtn.addEventListener('click', () => beginNew());
  deckNameInput.addEventListener('input', () => {
    editName = deckNameInput.value;
    if (editRefs) {
      // Only the save button's enablement depends on the name; avoid a full repaint.
      const legal = validateDeck(editRefs.map((r) => ({ cardId: r.cardId, rarity: r.rarity as CardRarity }))).ok;
      const owned = deckWithinCollection(
        editRefs.map((r) => ({ cardId: r.cardId, rarity: r.rarity as CardRarity })),
        state?.collection ?? [],
      );
      saveBtn.disabled = !legal || !owned || editName.trim().length === 0;
    }
  });
  saveBtn.addEventListener('click', () => saveDeck());
  backBtn.addEventListener('click', () => {
    editRefs = null;
    render();
  });
  openPackBtn.addEventListener('click', () => {
    // Guarded by disabled state, but re-check: only a reserved account with a pack
    // opens (the server rules authoritatively; the reveal + fresh state come back).
    if (deps.onOpenPack && state?.reserved && (state?.packs ?? 0) > 0) deps.onOpenPack();
  });
  buyPacksBtn.addEventListener('click', () => {
    if (deps.onBuyPack && state?.reserved) deps.onBuyPack();
  });
  duelWagerEl.addEventListener('input', () => {
    const wager = selectedDuelWager();
    duelCreateEl.disabled = selectedDuelDeck() === undefined || wager === undefined;
    duelCreateEl.textContent = wager && wager > 0 ? `List ${wager.toLocaleString('en-US')} gold duel` : 'List honor duel';
  });
  duelDeckEl.addEventListener('change', () => {
    selectedDuelDeckIndex = selectedDuelDeck() ?? null;
    if (tabs.value() === 'duels') renderDuelsTab();
  });
  challengeDeckEl.addEventListener('change', () => {
    selectedChallengeDeckIndex = selectedChallengeDeck() ?? null;
  });
  duelCreateEl.addEventListener('click', () => {
    const deckIndex = selectedDuelDeck();
    const wager = selectedDuelWager();
    if (deckIndex !== undefined && wager !== undefined) deps.onCreateDuelListing?.(deckIndex, wager);
  });
  challengeDeckEl.addEventListener('change', () => {
    if (tabs.value() === 'challenges') renderChallengesTab();
  });

  return {
    open: win.open,
    close: win.close,
    isOpen: win.isOpen,
    setState(next: CardBinderState) {
      state = next;
      if (next.starterRecipe) selectedRecipeId = next.starterRecipe;
      // A fresh authoritative state supersedes a pending confirmation (it arrives
      // right after a choice or on reopen), so retire the dialog.
      confirmPrompt.hide();
      // Every entry point converges here. A fresh reserved player lands directly on
      // the decision instead of seeing an empty collection with no explanation.
      if (!next.reserved || next.starterEligible || next.starterRecipe == null) {
        tabs.set('collection');
      }
      // A fresh authoritative state supersedes any in-progress edit (it only ever
      // arrives on open or right after a save), so drop the working copy and repaint.
      editRefs = null;
      render();
    },
    setDuelListings(next: CardDuelListingsState) {
      duelListings = next;
      if (tabs.value() === 'duels') renderDuelsTab();
    },
  };
}

/** `cardDef` that returns `null` instead of throwing on an unknown/legacy id (defensive over wire data). */
function safeCardDef(cardId: string, rarity: string) {
  try {
    return cardDef(cardId, rarity as CardRarity);
  } catch {
    return null;
  }
}
